<!--
 * @Author: AI Assistant
 * @Date: 2026-01-15
 * @Description: PWA 添加到桌面提示（PC端右下角卡片 / 移动端底部抽屉）
 * @FilePath: /weryai-web/components/Tool/PwaPromptCard.vue
-->

<template>
    <!--  PC端：右下角卡片 -->
    <div v-if="showPrompt && !isMobileDevice" class="fixed right-4 bottom-4 z-[999]">
        <!-- 整体容器 -->
        <div class="pwa-prompt-wrapper">
            <!-- 关闭按钮 - 整体右上角 -->
            <button class="pwa-close-btn" @click="closePrompt">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            <!-- 图片装饰区域 - 溢出到卡片上方 -->
            <div class="pwa-images-container">
                <!-- 光晕图 - 最底层 z-index: 1 -->
                <img src="https://static.weryai.com/cms/addlight.webp" alt="PWA 引导光晕图" class="pwa-light-img" />
                <!-- 左边金币 - 中间层 z-index: 2 -->
                <img src="https://static.weryai.com/cms/addcionleft.webp" alt="金币装饰图" class="pwa-coin-left" />
                <!-- 右边金币 - 中间层 z-index: 2 -->
                <img src="https://static.weryai.com/cms/addgiftright.webp" alt="金币装饰图" class="pwa-coin-right" />
                <!-- 礼物图 - 最上层 z-index: 3 -->
                <img src="https://static.weryai.com/cms/addgift.webp" alt="礼物装饰图" class="pwa-gift-img" />
            </div>

            <!-- 卡片框 - 在图片下方 z-index: 4 -->
            <div class="pwa-prompt-card">
                <!-- 文字内容 -->
                <div class="text-center px-4 pt-3">
                    <h3 class="text-white text-base font-semibold">{{ t('signIn.pwaPromptTitle') }}</h3>
                    <p class="text-white/70 text-xs mt-1">{{ t('signIn.pwaPromptDesc') }}</p>
                </div>

                <!-- 添加按钮 -->
                <button class="pwa-add-btn" @click="handleAdd">
                    {{ t('signIn.pwaPromptBtn') }}
                </button>
            </div>
        </div>
    </div>

    <!-- 移动端：底部抽屉 -->
    <Teleport to="body">
        <Transition name="mobile-drawer">
            <div v-if="showPrompt && isMobileDevice" class="pwa-mobile-overlay" @click.self="handleCloseMobileDrawer">
                <div class="pwa-mobile-drawer" :class="{ 'pwa-mobile-drawer--guide': showMobileGuideSteps }">
                    <!-- 关闭按钮 -->
                    <button class="pwa-mobile-close" @click="handleCloseMobileDrawer">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <!--初始视图 -->
                    <div v-if="!showMobileGuideSteps" class="pwa-mobile-content">
                        <!-- W 图标 -->
                        <img src="https://static.weryai.com/cms/mobileweAI.webp" alt="微鲤AI" class="pwa-mobile-icon" />

                        <!-- 标题 -->
                        <h3 class="pwa-mobile-title">{{ t('signIn.pwaPromptTitle') }}</h3>

                        <!-- 描述 -->
                        <p class="pwa-mobile-desc">{{ t('signIn.pwaPromptDesc') }}</p>

                        <!-- 添加按钮 -->
                        <button class="pwa-mobile-btn" @click="handleMobileAdd">
                            {{ t('signIn.pwaPromptBtn') }}
                        </button>
                    </div>

                    <!--  引导步骤视图  -->
                    <div v-else class="pwa-mobile-guide">
                        <!-- 顶部标题区域 -->
                        <div class="pwa-mobile-guide-header">
                            <h3 class="pwa-mobile-guide-title">{{ t('signIn.pwaGuideTitle') }}</h3>
                            <p class="pwa-mobile-guide-desc">{{ t('signIn.pwaGuideDesc') }}</p>
                        </div>

                        <!-- 可滚动的步骤列表 -->
                        <div class="pwa-mobile-guide-steps">
                            <div v-for="(step, index) in currentSteps" :key="index" class="pwa-mobile-step">
                                <!-- 步骤标题行 -->
                                <div class="pwa-step-header">
                                    <span class="pwa-step-number">{{ index + 1 }}</span>
                                    <span class="pwa-step-text">{{ step.title }}</span>
                                </div>
                                <!-- 步骤图片 -->
                                <img :src="step.image" :alt="step.title" class="pwa-step-image" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>

    <!-- PWA 引导弹窗 -->
    <ToolPwaGuideModal :visible="showGuideModal" @close="closeGuideModal" />
</template>

<script setup lang="ts">
const { t } = useI18n()

// 使用全局 PWA 提示状态管理
const {
    pwaPromptVisible: showPrompt,
    pwaGuideModalVisible: showGuideModal,
    closePwaPrompt: closePrompt,
    openPwaGuideModal,
    closePwaGuideModal: closeGuideModal,
    isMobile,
    canPromptInstall,
    promptInstall,
} = usePwaPrompt()

// 检测是否为移动设备（使用窗口宽度，与 SignInButton 保持一致）
const isMobileDevice = ref(false)
onMounted(() => {
    isMobileDevice.value = window.innerWidth < 768
})

// 移动端引导步骤显示状态
const showMobileGuideSteps = ref(false)

// 检测是否为移动端Safari浏览器
const isMobileSafari = (): boolean => {
    if (!import.meta.client) return false
    const ua = navigator.userAgent
    // Safari: 包含Safari但不包含Chrome/CriOS
    return /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|Chrome/.test(ua)
}

// Safari iOS 引导步骤数据
const safariSteps = computed(() => [
    {
        title: t('signIn.pwaStepSafari1'),
        image: 'https://static.weryai.com/cms/mobilesa1.webp'
    },
    {
        title: t('signIn.pwaStepSafari2'),
        image: 'https://static.weryai.com/cms/mobilesa2.webp'
    },
    {
        title: t('signIn.pwaStepSafari3'),
        image: 'https://static.weryai.com/cms/mobilesa3.webp'
    },
    {
        title: t('signIn.pwaStepSafari4'),
        image: 'https://static.weryai.com/cms/mobilesa4.webp'
    },
    {
        title: t('signIn.pwaStepSafari5'),
        image: 'https://static.weryai.com/cms/mobilesa5.webp'
    },
])

// iOS Chrome 引导步骤数据（步骤文案相同，图片不同）
const chromeSteps = computed(() => [
    {
        title: t('signIn.pwaStepSafari1'),
        image: 'https://static.weryai.com/cms/icmobilesa1.webp'
    },
    {
        title: t('signIn.pwaStepSafari2'),
        image: 'https://static.weryai.com/cms/icmobilesa2.webp'
    },
    {
        title: t('signIn.pwaStepSafari3'),
        image: 'https://static.weryai.com/cms/icmobilesa3.webp'
    },
    {
        title: t('signIn.pwaStepSafari4'),
        image: 'https://static.weryai.com/cms/icmobilesa4.webp'
    },
    {
        title: t('signIn.pwaStepSafari5'),
        image: 'https://static.weryai.com/cms/icmobilesa5.webp'
    },
])

// 根据浏览器类型选择显示的步骤
const currentSteps = computed(() => {
    return isMobileSafari() ? safariSteps.value : chromeSteps.value
})

// PC端点击添加 - 打开引导弹窗
const handleAdd = () => {
    openPwaGuideModal()
}

// 移动端点击添加
const handleMobileAdd = async () => {
    // Android 设备且支持原生安装提示
    if (canPromptInstall()) {
        const installed = await promptInstall()
        if (installed) {
            // 安装成功，关闭抽屉
            closePrompt()
        }
        // 用户拒绝安装，保持抽屉打开，可以再次尝试或关闭
        return
    }

    // iOS 或其他不支持原生安装的设备，显示引导步骤
    showMobileGuideSteps.value = true
}

// 关闭移动端抽屉时重置状态
const handleCloseMobileDrawer = () => {
    closePrompt()
    showMobileGuideSteps.value = false
}
</script>

<style scoped>
/* 整体容器 */
.pwa-prompt-wrapper {
    position: relative;
    width: 220px;
}

/* 图片装饰容器 - 定位在卡片上方 */
.pwa-images-container {
    position: absolute;
    top: -80px;
    left: 50%;
    transform: translateX(-50%);
    width: 220px;
    height: 130px;
    pointer-events: none;
}

/* 光晕图 - 最底层 */
.pwa-light-img {
    position: absolute;
    top: -34px;
    left: 50%;
    transform: translateX(-50%);
    width: 220px;
    height: auto;
    z-index: 1;
}

/* 左边金币 - 中间层 */
.pwa-coin-left {
    position: absolute;
    left: 0;
    top: 50px;
    width: 92px;
    height: auto;
    z-index: 2;
}

/* 右边金币 - 中间层 */
.pwa-coin-right {
    position: absolute;
    right: 0;
    top: 21px;
    width: 134px;
    height: auto;
    z-index: 2;
}

/* 礼物图 - 最上层 */
.pwa-gift-img {
    position: absolute;
    top: 25px;
    left: 50%;
    transform: translateX(-50%);
    width: 90px;
    height: auto;
    z-index: 3;
}

/* 卡片框 - 在图片下方 */
.pwa-prompt-card {
    position: relative;
    width: 100%;
    margin-top: 85px;
    background: linear-gradient(180deg, #070F0B 0%, #0C3A23 100%);
    border-radius: 15px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    z-index: 4;
    padding-bottom: 12px;
}

/* 关闭按钮 - 整体右上角 */
.pwa-close-btn {
    position: absolute;
    right: 0;
    top: -30px;
    width: 24px;
    height: 24px;
    border-radius: 100px;
    background: rgba(255, 255, 255, 0.20);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: background-color 0.2s;
    z-index: 10;
}

.pwa-close-btn:hover {
    background: rgba(255, 255, 255, 0.30);
}

/* 添加按钮 */
.pwa-add-btn {
    display: block;
    margin: 12px auto 0;
    padding: 10px 0;
    width: calc(100% - 32px);
    /* 卡片宽度减去两边各 16px 间距 */
    /* 或者使用固定宽度 */
    /* width: 180px; */
    background: #99F768;
    color: black;
    font-weight: 500;
    border-radius: 21px;
    transition: background-color 0.2s;
    box-shadow: 0 0 30px 0 rgba(163, 230, 53, 0.30);
}

.pwa-add-btn:hover {
    background: #7dd956;
}

/* ==================== 移动端底部抽屉样式 ==================== */

/* 遮罩层 */
.pwa-mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    align-items: flex-end;
}

/* 抽屉容器 */
.pwa-mobile-drawer {
    position: relative;
    width: 100%;
    background: url('https://static.weryai.com/growth/image/b449340ab3337479db32226d65e29986.webp') no-repeat top center;
    background-size: 100% auto;
    background-color: #0d0e12;
    border-radius: 20px 20px 0 0;
    padding: 20px 16px 32px;
    padding-bottom: calc(32px + env(safe-area-inset-bottom));
}

/* 关闭按钮 */
.pwa-mobile-close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.6);
}

/* 内容区域 */
.pwa-mobile-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

/* W 图标 */
.pwa-mobile-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
}

/* 标题 */
.pwa-mobile-title {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 8px;
}

/* 描述 */
.pwa-mobile-desc {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 24px;
}

/* 添加按钮 */
.pwa-mobile-btn {
    width: 100%;
    max-width: 280px;
    padding: 14px 0;
    background: #99F768;
    color: #000;
    font-size: 16px;
    font-weight: 500;
    border-radius: 25px;
    box-shadow: 0 0 30px 0 rgba(163, 230, 53, 0.30);
}

/* 抽屉动画 */
.mobile-drawer-enter-active,
.mobile-drawer-leave-active {
    transition: opacity 0.3s ease;
}

.mobile-drawer-enter-active .pwa-mobile-drawer,
.mobile-drawer-leave-active .pwa-mobile-drawer {
    transition: transform 0.3s ease;
}

.mobile-drawer-enter-from,
.mobile-drawer-leave-to {
    opacity: 0;
}

.mobile-drawer-enter-from .pwa-mobile-drawer,
.mobile-drawer-leave-to .pwa-mobile-drawer {
    transform: translateY(100%);
}

/* ==================== 移动端引导步骤样式 ==================== */

/* 引导步骤模式下的抽屉 - 高度为屏幕一半 */
.pwa-mobile-drawer--guide {
    height: 60vh;
    max-height: 60vh;
    display: flex;
    flex-direction: column;
}

/* 引导步骤容器 */
.pwa-mobile-guide {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* 顶部标题区域 - 固定 */
.pwa-mobile-guide-header {
    padding: 0 0 16px;
    text-align: center;
    flex-shrink: 0;
}

.pwa-mobile-guide-title {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 4px;
}

.pwa-mobile-guide-desc {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.6);
}

/* 步骤列表 - 可滚动 */
.pwa-mobile-guide-steps {
    flex: 1;
    overflow-y: auto;
    padding-bottom: env(safe-area-inset-bottom);
}

/* 单个步骤 */
.pwa-mobile-step {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 12px;
}

.pwa-mobile-step:last-child {
    margin-bottom: 0;
}

/* 步骤标题行 */
.pwa-step-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

/* 步骤序号 */
.pwa-step-number {
    width: 26px;
    height: 26px;
    min-width: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(180deg, #A3E635 0%, #65A30D 100%);
    border-radius: 6px;
    color: #000;
    font-weight: 600;
    font-size: 14px;
}

/* 步骤文字 */
.pwa-step-text {
    font-size: 14px;
    color: #fff;
    font-weight: 500;
}

/* 步骤图片 */
.pwa-step-image {
    width: 100%;
    border-radius: 8px;
}
</style>
